import { supabase } from '../supabase';

/** Phase C.7a — the ONE write path an authorized Team Admin/Head Coach
 * (or Program authority) uses to record a manual Team Testing result.
 * Team context (teamId) is supplied here — the Station is opened FROM a
 * specific Team — but independently re-verified server-side (049),
 * along with the athlete's active roster membership on that exact team;
 * never trust this call's own inputs as authorization.
 *
 * sessionId is OPTIONAL and should be kept in TRANSIENT component state
 * only (never persisted, never looked up by athlete+date) — omit it for
 * the athlete's first attempt during this station visit; pass back the
 * `session_id` this function just returned if the SAME athlete returns
 * later in the SAME visit for another attempt, so it correctly
 * accumulates into one testing occurrence instead of starting a new one
 * every time. A page refresh losing that continuity is expected and
 * correct for this phase (049's own header explains why) — the next
 * attempt simply starts an equally valid new occurrence.
 *
 * captureMethod (C.7a.1) — HOW this one result was actually measured:
 * 'manual' (a coach's own physical stopwatch, or any non-timed test) or
 * 'app_stopwatch' (the tablet itself timed it, via performance.now() —
 * see TeamTestingStation.js). Defaults to 'manual'. Server-side (049)
 * independently re-validates this against the same two-value allow-list
 * — never trusted as authorization or provenance beyond "how," and it
 * never affects source_type ('coach', fixed) or verification_status
 * ('personal', fixed) in any way.
 *
 * Testing Occurrence / Attempt Protocol (059) — the response now also
 * carries occurrence_id/occurrence_status/valid_attempt_count/
 * planned_max_attempts, so the Station learns "are we done now" from
 * this same round trip, with no second query. The server (059) is the
 * ONLY authority on the attempt cap — if the occurrence is already
 * complete, this call rejects with an error rather than ever silently
 * accepting an over-the-limit attempt; the UI's own disabled-state is a
 * convenience, never the actual enforcement.
 *
 * Returns { session_id, attempt_id, attempt_number, occurrence_id,
 * occurrence_status, valid_attempt_count, planned_max_attempts }. */
export async function recordTeamTestAttempt({ teamId, athleteId, testType, resultValue, testDate, sessionId, captureMethod }) {
  const { data, error } = await supabase.rpc('record_team_test_attempt', {
    p_team_id: teamId,
    p_athlete_id: athleteId,
    p_test_type: testType,
    p_result_value: resultValue,
    p_test_date: testDate,
    p_session_id: sessionId || null,
    p_capture_method: captureMethod || 'manual',
  });
  if (error) throw error;
  return data?.[0] || null;
}

/** Testing Occurrence / Attempt Protocol (059) — ordinary coaching
 * discretion: adjusts the planned attempt maximum for an ACTIVE
 * occurrence (3 -> 5, or lowering it back down as far as the current
 * valid-attempt count permits). No reason is required or accepted —
 * this is deliberately distinct from reopenTestOccurrence() below,
 * which is for a COMPLETED occurrence and does require one. Team-only;
 * the server rejects a Personal-context occurrence outright. Returns
 * the updated test_occurrences row. */
export async function changeTestOccurrenceMax({ occurrenceId, newMax, note }) {
  const { data, error } = await supabase.rpc('change_test_occurrence_max', {
    p_occurrence_id: occurrenceId,
    p_new_max: newMax,
    p_note: note || null,
  });
  if (error) throw error;
  return data;
}

/** Testing Occurrence / Attempt Protocol (059) — an authorized coach
 * explicitly ends an ACTIVE occurrence before its planned maximum is
 * reached (e.g. "New PR, you're done."). Never triggered automatically
 * by a PR — the coach always makes this call deliberately. Returns the
 * updated test_occurrences row. */
export async function endTeamTestOccurrenceEarly({ occurrenceId, reasonCode, note }) {
  const { data, error } = await supabase.rpc('end_test_occurrence_early', {
    p_occurrence_id: occurrenceId,
    p_reason_code: reasonCode || null,
    p_note: note || null,
  });
  if (error) throw error;
  return data;
}

/** Testing Occurrence / Attempt Protocol (059) — an authorized coach
 * explicitly reopens a COMPLETED occurrence with a strictly higher
 * planned maximum than before. Team-only, never available for Personal
 * Testing. Supports being called more than once against the same
 * occurrence (3/3 -> reopen to 4 -> reopen to 5) — each call is its own
 * independently-authorized, independently-reasoned action. Returns the
 * updated test_occurrences row. */
export async function reopenTeamTestOccurrence({ occurrenceId, newMax, reasonCode, note }) {
  const { data, error } = await supabase.rpc('reopen_test_occurrence', {
    p_occurrence_id: occurrenceId,
    p_new_max: newMax,
    p_reason_code: reasonCode,
    p_note: note || null,
  });
  if (error) throw error;
  return data;
}

/** Test Result Correction/Invalidation (055) — marks a Team-sourced
 * attempt invalidated, with no replacement. Authorization (team_admin/
 * head_coach of THAT attempt's own team, Program authority, or platform
 * admin — never assistant_coach) is derived server-side entirely from
 * the attempt's own session; no team_id is passed here; there is
 * nothing for the client to get wrong. Returns the updated
 * test_attempts row. */
export async function invalidateTeamTestAttempt({ attemptId, reason, note }) {
  const { data, error } = await supabase.rpc('invalidate_team_test_attempt', {
    p_attempt_id: attemptId,
    p_reason: reason,
    p_note: note || null,
  });
  if (error) throw error;
  return data;
}

/** Test Result Correction/Invalidation (055) — atomically invalidates a
 * Team-sourced attempt (or reuses its existing invalidation if it was
 * already invalidated with no replacement yet, 055 section 6) and
 * inserts a new valid replacement in the SAME session, same test type,
 * next sequential attempt_number, preserving the original's
 * capture_method. Returns { session_id, attempt_id, attempt_number } for
 * the NEW replacement row. */
export async function correctTeamTestAttempt({ attemptId, correctedResultValue, reason, note }) {
  const { data, error } = await supabase.rpc('correct_team_test_attempt', {
    p_attempt_id: attemptId,
    p_corrected_result_value: correctedResultValue,
    p_reason: reason,
    p_note: note || null,
  });
  if (error) throw error;
  return data?.[0] || null;
}
